import { useMemo } from 'react'
import { endOfMonth, endOfWeek, startOfMonth, startOfWeek } from 'date-fns'
import { get } from 'lodash'
import { useFetchWorkOrderListQuery } from 'api/work-order/fetchWorkOrderList'
import { formatDateToBEFormatDateFns } from 'utils/date'
import { RECURRENCE } from 'settings/constants/service'
import { RECURRENCE_TO_HEATMAP_VALUE } from 'settings/constants/workOrder'

const RECURRING_UPPER = RECURRENCE.RECURRING.toUpperCase()

// Per-date WO counts covering `visibleMonth`, from the Sunday on-or-before the 1st
// through the Saturday on-or-after the last day of the month.
export const useServiceWorkOrderCountsByMonth = ({ configuredServiceId, visibleMonth }) => {
  const monthStart = useMemo(() => (visibleMonth ? startOfWeek(startOfMonth(visibleMonth), { weekStartsOn: 0 }) : null), [visibleMonth])
  const monthEnd = useMemo(() => (visibleMonth ? endOfWeek(endOfMonth(visibleMonth), { weekStartsOn: 0 }) : null), [visibleMonth])

  const startDate = monthStart ? formatDateToBEFormatDateFns(monthStart) : null
  const endDate = monthEnd ? formatDateToBEFormatDateFns(monthEnd) : null

  const { data, isFetching } = useFetchWorkOrderListQuery(
    {
      configuredServiceIds: configuredServiceId ? [configuredServiceId] : [],
      startDate,
      endDate,
    },
    { skip: !configuredServiceId || !startDate || !endDate }
  )

  const countsByDate = useMemo(() => {
    const raw = new Map()
    const workOrders = get(data, 'workOrders', [])

    workOrders.forEach(wo => {
      const rawDate = get(wo, 'serviceDate')
      if (!rawDate) return
      const dateKey = typeof rawDate === 'string' ? rawDate.split('T')[0] : null
      if (!dateKey) return

      const current = raw.get(dateKey) || { total: 0, unassigned: 0, recurring: 0, request: 0 }
      current.total += 1
      if (!get(wo, 'routeId')) current.unassigned += 1
      const isRecurring = get(wo, 'pricedServiceType', '').toUpperCase() === RECURRING_UPPER
      if (isRecurring) current.recurring += 1
      else current.request += 1
      raw.set(dateKey, current)
    })

    const result = new Map()
    raw.forEach((v, key) => {
      let heatmapValue = RECURRENCE_TO_HEATMAP_VALUE.EMPTY
      if (v.recurring > 0 && v.request === 0) heatmapValue = RECURRENCE_TO_HEATMAP_VALUE.RECURRING
      else if (v.recurring === 0 && v.request > 0) heatmapValue = RECURRENCE_TO_HEATMAP_VALUE.ON_REQUEST
      else if (v.recurring > 0 && v.request > 0) heatmapValue = RECURRENCE_TO_HEATMAP_VALUE.MIXED
      result.set(key, { total: v.total, unassigned: v.unassigned, heatmapValue })
    })
    return result
  }, [data])

  return { countsByDate, isFetching }
}
